
import { Link } from 'react-router-dom';

interface Package {
  id: string;
  name: string;
  price: number;
  maxPrice: number;
  targetReturn: string;
  timeline: string;
  description: string;
  features: string[];
  investments: string[];
  benefits: string[];
  ideal: string;
  popular?: boolean;
}

interface PackageCardProps {
  package: Package;
}

export default function PackageCard({ package: pkg }: PackageCardProps) {
  const handleInvestNow = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: pkg.price,
          currency: 'usd',
          description: `${pkg.name} Investment Package`,
          metadata: {
            package_id: pkg.id,
            package_name: pkg.name,
            type: 'investment_package'
          }
        })
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg p-8 relative ${pkg.popular ? 'ring-2 ring-emerald-500' : ''}`}>
      {pkg.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
        <div className="text-4xl font-bold text-emerald-600 mb-2">
          ${pkg.price.toLocaleString()}
        </div>
        <p className="text-gray-600">Minimum Investment</p>
        <p className="text-sm text-gray-500 mt-1">Up to ${pkg.maxPrice.toLocaleString()}</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-600">Target Return:</span>
          <span className="font-semibold text-emerald-600">{pkg.targetReturn}</span>
        </div>
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-600">Timeline:</span>
          <span className="font-semibold">{pkg.timeline}</span>
        </div>
      </div>

      <p className="text-gray-600 mb-8">{pkg.description}</p>

      <div className="mb-8">
        <h4 className="font-semibold text-gray-900 mb-4">Key Features:</h4>
        <ul className="space-y-2">
          {pkg.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <i className="ri-check-line text-emerald-500 text-lg mr-2 mt-0.5"></i>
              <span className="text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-8">
        <h4 className="font-semibold text-gray-900 mb-4">Investment Types:</h4>
        <ul className="space-y-2">
          {pkg.investments.map((investment, index) => (
            <li key={index} className="flex items-start">
              <i className="ri-building-line text-emerald-500 text-lg mr-2 mt-0.5"></i>
              <span className="text-gray-600">{investment}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-8">
        <h4 className="font-semibold text-gray-900 mb-4">Benefits:</h4>
        <ul className="space-y-2">
          {pkg.benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <i className="ri-star-line text-emerald-500 text-lg mr-2 mt-0.5"></i>
              <span className="text-gray-600">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
        <p className="text-gray-600">{pkg.ideal}</p>
      </div>

      <button
        onClick={handleInvestNow}
        className={`w-full py-4 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
          pkg.popular
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-gray-900 text-white hover:bg-gray-800'
        }`}
      >
        Invest Now
      </button>

      <p className="text-xs text-gray-500 text-center mt-4">
        * Returns are projected based on historical performance and market analysis
      </p>
    </div>
  );
}
